import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const formatCurrency = (amount) =>
  `$${parseFloat(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getStatusBadge = (status) => {
  const map = {
    paid: { color: 'bg-success/10 text-success border-success/20', label: 'Paid', icon: 'CheckCircle' },
    partial: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Partial', icon: 'Clock' },
    overdue: { color: 'bg-error/10 text-error border-error/20', label: 'Overdue', icon: 'AlertTriangle' },
    sent: { color: 'bg-primary/10 text-primary border-primary/20', label: 'Sent', icon: 'Send' },
    draft: { color: 'bg-muted text-muted-foreground border-border', label: 'Draft', icon: 'FileText' },
    cancelled: { color: 'bg-muted text-muted-foreground border-border', label: 'Cancelled', icon: 'X' },
  };
  const s = (status || 'draft')?.toLowerCase();
  return map?.[s] || map?.draft;
};

const ReconcileModal = ({ invoice, payments, onClose, onSave }) => {
  const [note, setNote] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [existingNotes, setExistingNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);

  useEffect(() => {
    const fetchNotes = async () => {
      const { data } = await supabase?.from('reconciliation_notes')?.select('*')?.eq('invoice_id', invoice?.id)?.order('created_at', { ascending: false });
      setExistingNotes(data || []);
      setLoadingNotes(false);
    };
    fetchNotes();
  }, [invoice?.id]);

  const handleSave = async () => {
    if (!note?.trim()) return;
    setSaving(true);
    try {
      await supabase?.from('reconciliation_notes')?.insert({
        invoice_id: invoice?.id,
        payment_id: selectedPaymentId || null,
        note: note?.trim(),
        reconciled_by: 'Manager',
      });
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving note:', err);
    } finally {
      setSaving(false);
    }
  };

  const invoicePayments = payments?.filter(p => p?.invoice_id === invoice?.id);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border p-6 flex items-center justify-between sticky top-0 bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="GitMerge" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Reconcile Invoice</h2>
              <p className="text-sm text-muted-foreground">{invoice?.invoice_number} — {invoice?.customer?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Invoice Summary */}
          <div className="bg-muted/30 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Total Amount:</span><p className="font-semibold text-foreground">{formatCurrency(invoice?.total_amount)}</p></div>
            <div><span className="text-muted-foreground">Amount Paid:</span><p className="font-semibold text-success">{formatCurrency(invoice?.amount_paid)}</p></div>
            <div><span className="text-muted-foreground">Amount Due:</span><p className="font-bold text-error">{formatCurrency(invoice?.amount_due)}</p></div>
            <div><span className="text-muted-foreground">Due Date:</span><p className="font-medium text-foreground">{formatDate(invoice?.due_date)}</p></div>
          </div>

          {/* Matched Payments */}
          <div>
            <h3 className="font-semibold text-foreground mb-3">Payments Received ({invoicePayments?.length})</h3>
            {invoicePayments?.length === 0 ? (
              <p className="text-sm text-muted-foreground">No payments recorded for this invoice.</p>
            ) : (
              <div className="space-y-2">
                {invoicePayments?.map(pmt => (
                  <div key={pmt?.id} className="flex items-center justify-between p-3 bg-success/5 border border-success/20 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-foreground">{pmt?.payment_method || 'Payment'}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(pmt?.payment_date)} {pmt?.transaction_id ? `· ${pmt?.transaction_id?.substring(0, 20)}` : ''}</p>
                    </div>
                    <span className="font-bold text-success">{formatCurrency(pmt?.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Reconciliation Note */}
          <div>
            <h3 className="font-semibold text-foreground mb-3">Add Reconciliation Note</h3>
            {invoicePayments?.length > 0 && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-foreground mb-1">Link to Payment (Optional)</label>
                <select
                  value={selectedPaymentId}
                  onChange={e => setSelectedPaymentId(e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">No specific payment</option>
                  {invoicePayments?.map(pmt => (
                    <option key={pmt?.id} value={pmt?.id}>{formatDate(pmt?.payment_date)} — {formatCurrency(pmt?.amount)} ({pmt?.payment_method})</option>
                  ))}
                </select>
              </div>
            )}
            <textarea
              value={note}
              onChange={e => setNote(e?.target?.value)}
              placeholder="Add reconciliation note, discrepancy details, or action taken..."
              className="w-full min-h-[100px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
            <button
              onClick={handleSave}
              disabled={saving || !note?.trim()}
              className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <><div className="w-3 h-3 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />Saving...</> : <><Icon name="Save" size={14} color="currentColor" />Save Note</>}
            </button>
          </div>

          {/* Existing Notes */}
          {!loadingNotes && existingNotes?.length > 0 && (
            <div>
              <h3 className="font-semibold text-foreground mb-3">Reconciliation History</h3>
              <div className="space-y-2">
                {existingNotes?.map(n => (
                  <div key={n?.id} className="p-3 bg-muted/30 border border-border rounded-lg">
                    <p className="text-sm text-foreground">{n?.note}</p>
                    <p className="text-xs text-muted-foreground mt-1">{n?.reconciled_by} · {formatDate(n?.reconciled_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InvoiceReconciliationDashboard = () => {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [activeTab, setActiveTab] = useState('outstanding');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [invoicesRes, paymentsRes] = await Promise.all([
        supabase?.from('invoices')?.select('*, customer:customers(id, name, email, company), invoice_items(*), payments(*)')?.order('created_at', { ascending: false }),
        supabase?.from('payments')?.select('*, customer:customers(name, email)')?.order('payment_date', { ascending: false }),
      ]);
      if (invoicesRes?.error) throw invoicesRes?.error;
      if (paymentsRes?.error) throw paymentsRes?.error;
      setInvoices(invoicesRes?.data || []);
      setPayments(paymentsRes?.data || []);
    } catch (err) {
      console.error('Error fetching reconciliation data:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Stats
  const totalInvoiced = invoices?.reduce((s, inv) => s + parseFloat(inv?.total_amount || 0), 0);
  const totalPaid = invoices?.reduce((s, inv) => s + parseFloat(inv?.amount_paid || 0), 0);
  const totalOutstanding = invoices?.reduce((s, inv) => s + parseFloat(inv?.amount_due || 0), 0);
  const overdueInvoices = invoices?.filter(inv => {
    const status = inv?.invoice_status?.toLowerCase();
    return status === 'overdue' || (inv?.due_date && new Date(inv.due_date) < new Date() && !['paid', 'cancelled']?.includes(status));
  });
  const overdueAmount = overdueInvoices?.reduce((s, inv) => s + parseFloat(inv?.amount_due || 0), 0);
  const collectionRate = totalInvoiced > 0 ? Math.round((totalPaid / totalInvoiced) * 100) : 0;

  // Unmatched payments (payments without a corresponding fully-paid invoice)
  const unmatchedPayments = payments?.filter(pmt => {
    const inv = invoices?.find(i => i?.id === pmt?.invoice_id);
    return !inv || inv?.invoice_status?.toLowerCase() !== 'paid';
  });

  // Filter invoices
  const filteredInvoices = invoices?.filter(inv => {
    const matchesSearch = !searchQuery ||
      inv?.invoice_number?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      inv?.customer?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      inv?.customer?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const status = inv?.invoice_status?.toLowerCase();
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    const isOverdue = inv?.due_date && new Date(inv.due_date) < new Date() && !['paid', 'cancelled']?.includes(status);

    if (activeTab === 'outstanding') return matchesSearch && matchesStatus && !['paid', 'cancelled']?.includes(status);
    if (activeTab === 'overdue') return matchesSearch && isOverdue;
    if (activeTab === 'paid') return matchesSearch && status === 'paid';
    return matchesSearch && matchesStatus;
  });

  const handleReconcile = (invoice) => {
    setSelectedInvoice(invoice);
    setShowReconcileModal(true);
  };

  const statCards = [
    { label: 'Total Invoiced', value: formatCurrency(totalInvoiced), icon: 'FileText', color: 'text-primary', bg: 'bg-primary/10', sub: `${invoices?.length} invoices` },
    { label: 'Total Collected', value: formatCurrency(totalPaid), icon: 'CheckCircle', color: 'text-success', bg: 'bg-success/10', sub: `${collectionRate}% collection rate` },
    { label: 'Outstanding Balance', value: formatCurrency(totalOutstanding), icon: 'Clock', color: 'text-warning', bg: 'bg-warning/10', sub: `${invoices?.filter(i => !['paid','cancelled']?.includes(i?.invoice_status?.toLowerCase()))?.length} unpaid` },
    { label: 'Overdue Amount', value: formatCurrency(overdueAmount), icon: 'AlertTriangle', color: 'text-error', bg: 'bg-error/10', sub: `${overdueInvoices?.length} overdue invoices` },
  ];

  return (
    <>
      <Helmet><title>Invoice Reconciliation Dashboard | RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-16 min-h-screen bg-background">
        <div className="px-6 py-6 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="GitMerge" size={22} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-foreground">Invoice Reconciliation</h1>
                <p className="text-sm text-muted-foreground">Track payments, match to invoices, and reconcile accounts</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-smooth"
            >
              <Icon name="RefreshCw" size={16} color="currentColor" />Refresh
            </button>
          </div>

          {/* Stats */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[1,2,3,4]?.map(i => <div key={i} className="bg-card rounded-lg border border-border p-4 animate-pulse h-24" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {statCards?.map((stat, i) => (
                <div key={i} className="bg-card rounded-lg border border-border shadow-elevation-1 p-4 hover:shadow-elevation-2 transition-smooth">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-caption text-muted-foreground">{stat?.label}</span>
                    <div className={`w-10 h-10 rounded-lg ${stat?.bg} flex items-center justify-center`}>
                      <Icon name={stat?.icon} size={20} className={stat?.color} />
                    </div>
                  </div>
                  <p className="text-2xl font-heading font-bold text-foreground">{stat?.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat?.sub}</p>
                </div>
              ))}
            </div>
          )}

          {/* Unmatched Payments Alert */}
          {unmatchedPayments?.length > 0 && (
            <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
              <div>
                <p className="font-semibold text-foreground">{unmatchedPayments?.length} payment(s) need reconciliation</p>
                <p className="text-sm text-muted-foreground">These payments are linked to invoices that are not yet fully paid. Review and reconcile below.</p>
              </div>
            </div>
          )}

          {/* Tabs + Filters */}
          <div className="bg-card rounded-xl border border-border shadow-elevation-1">
            <div className="border-b border-border p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                {[
                  { key: 'outstanding', label: 'Outstanding', count: invoices?.filter(i => !['paid','cancelled']?.includes(i?.invoice_status?.toLowerCase()))?.length },
                  { key: 'overdue', label: 'Overdue', count: overdueInvoices?.length },
                  { key: 'paid', label: 'Paid', count: invoices?.filter(i => i?.invoice_status?.toLowerCase() === 'paid')?.length },
                  { key: 'all', label: 'All', count: invoices?.length },
                ]?.map(tab => (
                  <button
                    key={tab?.key}
                    onClick={() => setActiveTab(tab?.key)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-smooth flex items-center gap-1.5 ${
                      activeTab === tab?.key ? 'bg-card text-foreground shadow-elevation-1' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab?.label}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === tab?.key ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    }`}>{tab?.count}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <div className="relative">
                  <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search invoices..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e?.target?.value)}
                    className="pl-9 pr-4 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring w-48"
                  />
                </div>
                {activeTab === 'all' && (
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e?.target?.value)}
                    className="px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="all">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="partial">Partial</option>
                    <option value="overdue">Overdue</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                )}
              </div>
            </div>

            {/* Invoice Table */}
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredInvoices?.length === 0 ? (
              <div className="p-12 text-center">
                <Icon name="FileText" size={40} color="var(--color-muted-foreground)" />
                <p className="text-muted-foreground mt-3">No invoices found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30 border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Invoice #</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Customer</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Date</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Due Date</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Total</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Paid</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Balance</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Payments</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredInvoices?.map(inv => {
                      const badge = getStatusBadge(inv?.invoice_status);
                      const isOverdue = inv?.due_date && new Date(inv.due_date) < new Date() && !['paid', 'cancelled']?.includes(inv?.invoice_status?.toLowerCase());
                      const invPayments = payments?.filter(p => p?.invoice_id === inv?.id);
                      const hasDiscrepancy = Math.abs(parseFloat(inv?.amount_paid || 0) - invPayments?.reduce((s, p) => s + parseFloat(p?.amount || 0), 0)) > 0.01;
                      return (
                        <tr key={inv?.id} className={`hover:bg-muted/30 transition-smooth ${isOverdue ? 'bg-error/5' : ''}`}>
                          <td className="px-4 py-3">
                            <span className="font-medium text-foreground">{inv?.invoice_number}</span>
                            {hasDiscrepancy && <span className="ml-2 text-xs text-warning" title="Payment discrepancy detected">⚠</span>}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground">{inv?.customer?.name || '—'}</p>
                            <p className="text-xs text-muted-foreground">{inv?.customer?.email || ''}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(inv?.created_at)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${isOverdue ? 'text-error font-medium' : 'text-muted-foreground'}`}>
                              {formatDate(inv?.due_date)}
                              {isOverdue && <span className="ml-1 text-xs">({Math.floor((new Date() - new Date(inv.due_date)) / 86400000)}d overdue)</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-foreground">{formatCurrency(inv?.total_amount)}</td>
                          <td className="px-4 py-3 text-right font-medium text-success">{formatCurrency(inv?.amount_paid)}</td>
                          <td className="px-4 py-3 text-right font-bold text-error">{formatCurrency(inv?.amount_due)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${badge?.color}`}>
                              <Icon name={badge?.icon} size={10} color="currentColor" />{badge?.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm font-medium ${invPayments?.length > 0 ? 'text-success' : 'text-muted-foreground'}`}>
                              {invPayments?.length}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleReconcile(inv)}
                              className="px-3 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-lg text-xs font-medium hover:bg-primary/20 transition-smooth flex items-center gap-1 mx-auto"
                            >
                              <Icon name="GitMerge" size={12} color="currentColor" />Reconcile
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Payments Section */}
          <div className="mt-6 bg-card rounded-xl border border-border shadow-elevation-1">
            <div className="border-b border-border p-4">
              <h2 className="font-heading font-semibold text-foreground flex items-center gap-2">
                <Icon name="Receipt" size={18} color="var(--color-primary)" />
                Recent Payments ({payments?.length})
              </h2>
            </div>
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : payments?.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="Receipt" size={32} color="var(--color-muted-foreground)" />
                <p className="text-muted-foreground mt-2">No payments recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30 border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Payment #</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Customer</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Invoice</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Date</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Method</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Source</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Amount</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Matched</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {payments?.slice(0, 20)?.map(pmt => {
                      const matchedInvoice = invoices?.find(i => i?.id === pmt?.invoice_id);
                      const isMatched = !!matchedInvoice;
                      return (
                        <tr key={pmt?.id} className="hover:bg-muted/30 transition-smooth">
                          <td className="px-4 py-3 font-medium text-foreground">{pmt?.payment_number || '—'}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{pmt?.customer?.name || '—'}</td>
                          <td className="px-4 py-3 text-sm text-primary">{matchedInvoice?.invoice_number || '—'}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(pmt?.payment_date)}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{pmt?.payment_method || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              pmt?.payment_source === 'stripe_online' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                            }`}>
                              {pmt?.payment_source === 'stripe_online' ? 'Online' : 'Manual'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-success">{formatCurrency(pmt?.amount)}</td>
                          <td className="px-4 py-3 text-center">
                            {isMatched ? (
                              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                            ) : (
                              <Icon name="AlertCircle" size={16} color="var(--color-warning)" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      {showReconcileModal && selectedInvoice && (
        <ReconcileModal
          invoice={selectedInvoice}
          payments={payments}
          onClose={() => setShowReconcileModal(false)}
          onSave={fetchData}
        />
      )}
    </>
  );
};

export default InvoiceReconciliationDashboard;
